import { useEffect, useRef, useState, useCallback } from "react";
import VideoScrubber from "@/components/VideoScrubber";
import SoapTiles from "@/components/SoapTiles";

const MAX_SCROLL = 3.5;

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

function deriveSection(p: number): string {
  if (p < 0.18) return "hero";
  if (p < 0.45) return "projects";
  if (p < 0.68) return "expertise";
  if (p < 1.15) return "about";
  return "contact";
}

export default function Home() {
  // The "intent" scroll value, updated immediately by gesture
  const scrollTargetRef = useRef<number>(0);
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  // The smoothed/lerped value used for rendering
  const lerpedRef = useRef<number>(0);
  const [lerped, setLerped] = useState<number>(0);

  const [activeSectionId, setActiveSectionId] = useState<string>("hero");

  // For programmatic navigation cancellation
  const navAnimRef = useRef<number | null>(null);
  const navCancelRef = useRef<boolean>(false);

  const cancelProgrammaticNav = useCallback(() => {
    if (navAnimRef.current !== null) {
      cancelAnimationFrame(navAnimRef.current);
      navAnimRef.current = null;
      navCancelRef.current = true;
    }
  }, []);

  // Gesture controller — runs once on mount
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    let lastTouchY = 0;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      cancelProgrammaticNav();
      const next = clamp(
        scrollTargetRef.current + e.deltaY * 0.0006,
        0,
        MAX_SCROLL
      );
      scrollTargetRef.current = next;
      setScrollProgress(next);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        lastTouchY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      e.preventDefault();
      cancelProgrammaticNav();
      const currentY = e.touches[0].clientY;
      const delta = lastTouchY - currentY;
      lastTouchY = currentY;
      const next = clamp(
        scrollTargetRef.current + delta * 0.0015,
        0,
        MAX_SCROLL
      );
      scrollTargetRef.current = next;
      setScrollProgress(next);
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [cancelProgrammaticNav]);

  // RAF loop — smooth lerp + section detection
  useEffect(() => {
    let raf: number;
    const loop = () => {
      const target = scrollTargetRef.current;
      const cur = lerpedRef.current;
      const diff = target - cur;
      if (Math.abs(diff) > 0.0001) {
        lerpedRef.current = cur + diff * 0.08;
        setLerped(lerpedRef.current);

        const section = deriveSection(lerpedRef.current);
        setActiveSectionId((prev) => (prev === section ? prev : section));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Programmatic navigation (exposed for future use)
  const handleNavigateToSection = useCallback(
    (targetScrollRatio: number) => {
      cancelProgrammaticNav();
      navCancelRef.current = false;

      const startVal = scrollTargetRef.current;
      const endVal = clamp(targetScrollRatio, 0, MAX_SCROLL);
      const startTime = performance.now();
      const duration = 1200;

      const easeInOutCubic = (p: number) =>
        p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

      const step = (now: number) => {
        if (navCancelRef.current) return;
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = easeInOutCubic(t);
        const v = startVal + (endVal - startVal) * eased;
        scrollTargetRef.current = v;
        setScrollProgress(v);
        if (t < 1) {
          navAnimRef.current = requestAnimationFrame(step);
        } else {
          navAnimRef.current = null;
        }
      };

      navAnimRef.current = requestAnimationFrame(step);
    },
    [cancelProgrammaticNav]
  );

  // Avoid unused-var warning while still exposing the helper for later steps
  useEffect(() => {
    (window as unknown as { __navigateToSection?: typeof handleNavigateToSection }).__navigateToSection =
      handleNavigateToSection;
  }, [handleNavigateToSection]);

  return (
    <main className="relative w-screen h-screen overflow-hidden bg-[#0A1A3A] text-white">
      <div className="relative w-full h-full overflow-hidden">
        {/* FIRST SCREEN */}
        <div
          id="first-screen"
          className="absolute inset-0 w-full h-full z-10"
        >
          {/* Video scrubber background */}
          <VideoScrubber scrollProgress={Math.min(1, lerped)} />

          {/* SoapTiles — three white pill shapes on left */}
          <SoapTiles scrollProgress={Math.min(1, lerped)} />

          {/* Hero title — right-middle, serif display with decorative lines.
              Slides out to the right as scroll progresses. */}
          <div
            className="absolute right-[5vw] top-[32vh] z-20 pointer-events-none max-w-[58vw] text-right"
            style={{
              transform: `translateX(${Math.pow(Math.min(1, lerped), 2) * 120}vw)`,
              opacity: 1 - Math.min(1, lerped),
              transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
              willChange: "transform, opacity",
            }}
          >
            {/* Top decorative row: serial + line */}
            <div className="flex items-center justify-end gap-3 mb-6">
              <span className="font-manrope text-white/70 text-[10px] uppercase tracking-[0.35em]">
                Vol. 01 — Field Notes
              </span>
              <span className="w-20 h-px bg-white/60" />
              <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
            </div>

            {/* Headline */}
            <h1
              className="font-playfair text-white leading-[1.02] tracking-[-0.015em]"
              style={{ fontSize: "clamp(48px, 5.6vw, 104px)" }}
            >
              <span className="block font-medium">Objects shaped</span>
              <span className="block italic font-medium">by quiet hands.</span>
            </h1>

            {/* Bottom decorative row: line + dot + caption */}
            <div className="flex items-center justify-end gap-3 mt-6">
              <span className="font-manrope text-white/60 text-[10px] uppercase tracking-[0.3em]">
                An Inner Circle Study
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
              <span className="w-28 h-px bg-white/50" />
            </div>

            {/* Right-edge vertical accent line */}
            <span className="absolute -right-4 top-1/2 -translate-y-1/2 w-px h-32 bg-white/40" />
            {/* Subtle bracket marks */}
            <span className="absolute -right-4 top-1/2 -translate-y-[68px] w-3 h-px bg-white/40" />
            <span className="absolute -right-4 top-1/2 translate-y-[68px] w-3 h-px bg-white/40" />
          </div>

          {/* Subtle bottom hint */}
          <div className="absolute bottom-6 right-8 z-20 flex items-center gap-3 text-white/70 font-manrope text-[11px] uppercase tracking-[0.25em] pointer-events-none">
            <span>Scroll</span>
            <span className="w-10 h-px bg-white/60" />
          </div>
        </div>

        {/* HEADER */}
        <div
          id="header-placeholder"
          className="absolute top-8 left-8 right-8 z-40 flex justify-between items-center"
        >
          <span className="font-michroma text-white text-sm tracking-widest">
            INNER CIRCLE
          </span>
          <nav className="hidden md:flex items-center gap-8 font-manrope text-white/80 text-xs uppercase tracking-[0.2em]">
            <a href="#projects" className="hover:text-white transition-colors cursor-pointer">
              Projects
            </a>
            <a href="#expertise" className="hover:text-white transition-colors cursor-pointer">
              Expertise
            </a>
            <a href="#about" className="hover:text-white transition-colors cursor-pointer">
              About
            </a>
            <a href="#contact" className="hover:text-white transition-colors cursor-pointer">
              Contact
            </a>
          </nav>
          <span className="font-manrope text-white/60 text-xs">
            scrollProgress: {lerped.toFixed(3)}
          </span>
        </div>

        {/* Debug overlay */}
        <div className="absolute top-2 right-2 z-50 font-manrope text-white/80 text-[10px] uppercase tracking-[0.2em] pointer-events-none bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-md">
          <div>progress: {lerped.toFixed(3)}</div>
          <div>section: {activeSectionId}</div>
        </div>
      </div>
    </main>
  );
}